import React from 'react';
import { Card } from '../Card';
import patterns from '../styles/patterns.module.css';
import styles from './BarChartCard.module.css';

export type BarChartTone = 'accent' | 'success' | 'info' | 'purple' | 'warning' | 'error';

export interface BarChartRow {
  label: React.ReactNode;
  value: number;
  display?: string;
  tone?: BarChartTone;
}

interface BarChartCardProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  rows: BarChartRow[];
  /** Explicit maximum for bar width calculation. Defaults to Math.max of all row values. */
  max?: number;
  emptyState?: React.ReactNode;
}

export function BarChartCard({
  title,
  subtitle,
  icon,
  action,
  rows,
  max,
  emptyState,
}: BarChartCardProps) {
  const resolvedMax = max ?? Math.max(...rows.map((r) => r.value), 1);

  return (
    <Card>
      <div className={styles.header}>
        <div>
          <div className={`${patterns.sectionLabel} ${styles.kicker}`}>
            {icon && <span className={styles.iconWrap}>{icon}</span>}
            {title}
          </div>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
        {action && <div className={styles.action}>{action}</div>}
      </div>

      {rows.length === 0 && emptyState ? (
        <div className={styles.empty}>{emptyState}</div>
      ) : (
        <div className={styles.table}>
          {rows.map((row, i) => {
            const widthPct = Math.round((row.value / resolvedMax) * 100);
            const tone = row.tone ?? 'accent';
            return (
              <div key={i} className={styles.row}>
                <span className={styles.label}>{row.label}</span>
                <div className={styles.track}>
                  <div
                    className={`${styles.bar} ${styles[`bar_${tone}`]}`}
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
                <span className={styles.value}>{row.display ?? String(row.value)}</span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
